import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import apiClient from "../api/client";
import polyline from "polyline-encoded";
import RouteMap from "./RouteMap";
import { getDefaultRunName } from "../utils/activityNames";
import "./ActivityIntakeModal.css";

const emptyForm = {
  name: "",
  distanceKm: "",
  durationMinutes: "",
  elevationGain: "",
  avgHr: "",
  maxHr: "",
  notes: "",
  perceivedEffort: ""
};

const ActivityImportModal = ({ isOpen, onClose, email = "", onSuccess = undefined, onError = undefined }) => {
  const [preview, setPreview] = useState(null);
  const [formState, setFormState] = useState(emptyForm);
  const [error, setError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [previewPolyline, setPreviewPolyline] = useState(null);
  const fileInputRef = useRef(null);

  const resetState = () => {
    setPreview(null);
    setFormState(emptyForm);
    setError(null);
    setIsUploading(false);
    setIsSaving(false);
    setSelectedFileName("");
    setPreviewPolyline(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  const encodePreviewPolyline = (points) => {
    if (!Array.isArray(points) || points.length < 2) {
      return null;
    }

    const cleaned = points
      .map((point) => {
        if (!point) return null;
        if (Array.isArray(point) && point.length >= 2) {
          const lat = Number(point[0]);
          const lng = Number(point[1]);
          if (Number.isNaN(lat) || Number.isNaN(lng)) {
            return null;
          }
          return [lat, lng];
        }
        if (typeof point === "string") {
          const [latStr, lngStr] = point.split(",");
          const lat = Number(latStr);
          const lng = Number(lngStr);
          if (Number.isNaN(lat) || Number.isNaN(lng)) {
            return null;
          }
          return [lat, lng];
        }
        return null;
      })
      .filter(Boolean);

    if (cleaned.length < 2) {
      return null;
    }

    try {
      return polyline.encode(cleaned);
    } catch (encodeError) {
      console.error("Failed to encode preview polyline", encodeError);
      return null;
    }
  };

  const derivePreviewPolyline = (data) => {
    if (!data) return null;
    if (data.route_polyline) {
      return data.route_polyline;
    }
    const pointData = data.route_points || data.points;
    return encodePreviewPolyline(pointData);
  };

  const applyPreviewDefaults = (data, fallbackName) => {
    if (!data) {
      setFormState(emptyForm);
      setPreviewPolyline(null);
      return;
    }

    const defaultName = getDefaultRunName(
      data.start_time || new Date().toISOString()
    );
    const inferredName =
      (data.name && data.name.trim()) ||
      defaultName ||
      (fallbackName && fallbackName.trim()) ||
      "";

    setFormState({
      name: inferredName,
      distanceKm:
        data.distance && !Number.isNaN(Number(data.distance))
          ? (data.distance / 1000).toFixed(2)
          : "",
      durationMinutes:
        data.moving_time && data.moving_time > 0
          ? (data.moving_time / 60).toFixed(1)
          : data.elapsed_time && data.elapsed_time > 0
            ? (data.elapsed_time / 60).toFixed(1)
            : "",
      elevationGain:
        data.total_elevation_gain !== null &&
        data.total_elevation_gain !== undefined
          ? Number(data.total_elevation_gain).toFixed(0)
          : "",
      avgHr:
        data.average_heartrate !== null && data.average_heartrate !== undefined
          ? Math.round(data.average_heartrate)
          : "",
      maxHr:
        data.max_heartrate !== null && data.max_heartrate !== undefined
          ? Math.round(data.max_heartrate)
          : "",
      notes: "",
      perceivedEffort: ""
    });

    setPreviewPolyline(derivePreviewPolyline(data));
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setError(null);
    setIsUploading(true);
    setSelectedFileName(file.name);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await apiClient.post(
        '/activities/import/preview',
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        }
      );

      setPreview(response.data.preview);
      applyPreviewDefaults(response.data.preview, file.name);
    } catch (uploadError) {
      console.error("Failed to generate preview", uploadError);
      const message =
        uploadError.response?.data?.code ||
        uploadError.response?.data?.error ||
        "Unable to parse the uploaded file.";
      setError(message);
      if (onError) {
        onError(message);
      }
      setPreview(null);
      applyPreviewDefaults(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const closeModal = () => {
    resetState();
    onClose();
  };

  const buildPayload = () => {
    const distanceKm = parseFloat(formState.distanceKm);
    if (Number.isNaN(distanceKm) || distanceKm <= 0) {
      throw new Error("Please enter a valid distance in kilometers.");
    }

    const durationMinutes = parseFloat(formState.durationMinutes);
    if (Number.isNaN(durationMinutes) || durationMinutes <= 0) {
      throw new Error("Please enter a valid duration in minutes.");
    }

    const distanceMeters = Math.round(distanceKm * 1000);
    const movingSeconds = Math.round(durationMinutes * 60);

    const defaultName = getDefaultRunName(
      preview?.start_time || new Date().toISOString()
    );
    const resolvedName =
      (formState.name && formState.name.trim()) ||
      defaultName ||
      (selectedFileName && selectedFileName.trim()) ||
      defaultName;
    const summaryPolyline =
      preview?.route_polyline ||
      previewPolyline ||
      encodePreviewPolyline(preview?.route_points || preview?.points);

    return {
      source_type: preview?.source_type || "gpx",
      name: resolvedName,
      distance: distanceMeters,
      moving_time: movingSeconds,
      elapsed_time: movingSeconds,
      total_elevation_gain:
        formState.elevationGain !== ""
          ? Number(formState.elevationGain)
          : null,
      average_heartrate:
        formState.avgHr !== "" ? Number(formState.avgHr) : null,
      max_heartrate:
        formState.maxHr !== "" ? Number(formState.maxHr) : null,
      start_date_local:
        preview?.start_time || new Date().toISOString().slice(0, 19),
      start_latlng: preview?.start_latlng || null,
      end_latlng: preview?.end_latlng || null,
      summary_polyline: summaryPolyline || null,
      notes: formState.notes,
      perceived_effort:
        formState.perceivedEffort !== ""
          ? Number(formState.perceivedEffort)
          : null,
      external_id: selectedFileName,
      is_manual: false
    };
  };

  const handleSave = async () => {
    try {
      setError(null);
      setIsSaving(true);
      const payload = buildPayload();
      await apiClient.post('/activities', payload);
      if (onSuccess) {
        onSuccess("Run imported successfully.");
      }
      closeModal();
    } catch (saveError) {
      console.error("Failed to save imported run", saveError);
      const message =
        saveError.message ||
        saveError.response?.data?.messages?.[0] ||
        saveError.response?.data?.error ||
        "Unable to save imported run.";
      setError(message);
      if (onError) {
        onError(message);
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="activity-modal-backdrop">
      <div className="activity-modal">
        <div className="activity-modal-header">
          <h2 className="activity-modal-title">Import GPX/TCX</h2>
          <button
            type="button"
            className="activity-modal-close"
            onClick={closeModal}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="activity-modal-body">
          <p className="activity-modal-subtitle">
            Upload a GPX or TCX file to generate a preview. You can adjust the
            derived stats before saving.
          </p>

          <div className="activity-dropzone">
            <h4>
              {selectedFileName
                ? `Selected: ${selectedFileName}`
                : "Choose a GPX or TCX file"}
            </h4>
            <p>Maximum size: 25 MB</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".gpx,.tcx"
              onChange={handleFileChange}
              aria-label="Upload activity file"
              disabled={isUploading}
            />
          </div>

          {isUploading && (
            <div className="activity-modal-help">
              Generating preview. This may take a few seconds...
            </div>
          )}

          {error && <div className="activity-modal-error">{error}</div>}

          {preview && (
            <>
              <div className="activity-modal-summary">
                <div className="activity-modal-summary-item">
                  <span className="activity-modal-summary-label">
                    Estimated Distance
                  </span>
                  <span className="activity-modal-summary-value">
                    {preview.distance
                      ? `${(preview.distance / 1000).toFixed(2)} km`
                      : "—"}
                  </span>
                </div>
                <div className="activity-modal-summary-item">
                  <span className="activity-modal-summary-label">
                    Moving Time
                  </span>
                  <span className="activity-modal-summary-value">
                    {preview.moving_time
                      ? `${Math.round(preview.moving_time / 60)} min`
                      : preview.elapsed_time
                        ? `${Math.round(preview.elapsed_time / 60)} min`
                        : "—"}
                  </span>
                </div>
                <div className="activity-modal-summary-item">
                  <span className="activity-modal-summary-label">
                    Elevation Gain
                  </span>
                  <span className="activity-modal-summary-value">
                    {preview.total_elevation_gain !== null &&
                    preview.total_elevation_gain !== undefined
                      ? `${Math.round(preview.total_elevation_gain)} m`
                      : "—"}
                  </span>
                </div>
                <div className="activity-modal-summary-item">
                  <span className="activity-modal-summary-label">
                    Samples
                  </span>
                  <span className="activity-modal-summary-value">
                    {preview.point_count}
                  </span>
                </div>
              </div>

              <p className="activity-modal-section-title">Route Preview</p>
              <RouteMap
                summaryPolyline={previewPolyline}
                height="240px"
                showPlaceholder
              />

              <p className="activity-modal-section-title">Adjust &amp; Save</p>

              <div className="activity-modal-field">
                <label htmlFor="import-name">Activity Name</label>
                <input
                  id="import-name"
                  type="text"
                  value={formState.name}
                  onChange={(event) =>
                    handleFieldChange("name", event.target.value)
                  }
                />
              </div>

              <div className="activity-modal-grid">
                <div className="activity-modal-field">
                  <label htmlFor="import-distance">Distance (km)</label>
                  <input
                    id="import-distance"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formState.distanceKm}
                    onChange={(event) =>
                      handleFieldChange("distanceKm", event.target.value)
                    }
                  />
                </div>
                <div className="activity-modal-field">
                  <label htmlFor="import-duration">Duration (minutes)</label>
                  <input
                    id="import-duration"
                    type="number"
                    min="0"
                    step="0.1"
                    value={formState.durationMinutes}
                    onChange={(event) =>
                      handleFieldChange("durationMinutes", event.target.value)
                    }
                  />
                </div>
                <div className="activity-modal-field">
                  <label htmlFor="import-elevation">Elevation Gain (m)</label>
                  <input
                    id="import-elevation"
                    type="number"
                    step="1"
                    value={formState.elevationGain}
                    onChange={(event) =>
                      handleFieldChange("elevationGain", event.target.value)
                    }
                  />
                </div>
                <div className="activity-modal-field">
                  <label htmlFor="import-avg-hr">Avg HR</label>
                  <input
                    id="import-avg-hr"
                    type="number"
                    min="0"
                    step="1"
                    value={formState.avgHr}
                    onChange={(event) =>
                      handleFieldChange("avgHr", event.target.value)
                    }
                  />
                </div>
                <div className="activity-modal-field">
                  <label htmlFor="import-max-hr">Max HR</label>
                  <input
                    id="import-max-hr"
                    type="number"
                    min="0"
                    step="1"
                    value={formState.maxHr}
                    onChange={(event) =>
                      handleFieldChange("maxHr", event.target.value)
                    }
                  />
                </div>
                <div className="activity-modal-field">
                  <label htmlFor="import-effort">Perceived Effort (1-10)</label>
                  <input
                    id="import-effort"
                    type="number"
                    min="1"
                    max="10"
                    step="1"
                    value={formState.perceivedEffort}
                    onChange={(event) =>
                      handleFieldChange("perceivedEffort", event.target.value)
                    }
                  />
                </div>
              </div>

              <div className="activity-modal-field">
                <label htmlFor="import-notes">Notes</label>
                <textarea
                  id="import-notes"
                  value={formState.notes}
                  onChange={(event) =>
                    handleFieldChange("notes", event.target.value)
                  }
                  placeholder="Optional comments about this run"
                />
              </div>
            </>
          )}
        </div>

        <div className="activity-modal-footer">
          <button
            type="button"
            className="activity-modal-button secondary"
            onClick={closeModal}
          >
            Cancel
          </button>
          <button
            type="button"
            className="activity-modal-button primary"
            onClick={handleSave}
            disabled={isSaving || isUploading || !preview}
          >
            {isSaving ? "Saving…" : "Save Activity"}
          </button>
        </div>
      </div>
    </div>
  );
};

ActivityImportModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  email: PropTypes.string,
  onSuccess: PropTypes.func,
  onError: PropTypes.func
};

export default ActivityImportModal;
